import type { QuestionKey } from './questionnaire.types';

/**
 * The extra hard-requirement questions, served by `/questionnaire/{token}/hard_requirement_questions/`.
 *
 * These are gates rather than preferences: the backend scores every answer and a candidate who
 * misses one is not offered a scheduler link. They are a separate endpoint, a separate list and a
 * separate key space from `CandidateRequirementsDTO` — see `HardRequirementQuestionKey` below.
 */
export enum HardRequirementQuestionKey {
  EDUCATION = 'education',
  CERTIFICATE = 'certificate',
  LANGUAGE = 'language',
  EXPERIENCE_MONTHS = 'experience_months',
  DAILY_WORKING_TIME = 'daily_working_time',
  SHIFT = 'shift',
  OWN_TRANSPORT = 'own_transport',
  DRIVER_LICENSE = 'driver_license',
  PHYSICAL_DEMAND = 'physical_demand',
  PACE_REPETITIVE = 'pace_repetitive',
  OUTDOOR_COLD = 'outdoor_cold',
  HYGIENE_RULES = 'hygiene_rules',
  START_AVAILABILITY = 'start_availability',
  WORKED_LAST_6_MONTHS = 'worked_last_6_months',
}

/**
 * How the backend stores a hard requirement — **not** how the questionnaire asks about it.
 *
 * Every hard question is asked the same way, as the question plus yes or no, so nothing in the UI
 * switches on this. It is carried because the payload sends it and because the PUT's scoring is
 * shaped by it.
 */
export enum HardRequirementInputType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  DATE = 'date',
  CHOICE = 'choice',
  ARRAY = 'array',
}

/**
 * What the candidate must satisfy, in the shape the question's own input type implies:
 *
 * - `array`   → the values that must all be present (`['heftruck', 'ept']`)
 * - `boolean` → always `true`
 * - `choice`  → a threshold, e.g. `{ min_level: 'hbo' }` — not the list of options
 * - `number`  → e.g. `{ min_months: 6 }`
 */
export type HardRequirementJobRequirements = string[] | boolean | Record<string, unknown> | null;

/**
 * The answer a candidate gives: whether they meet the requirement.
 *
 * Always a boolean, whatever the question's `input_type` — the candidate confirms or denies the
 * requirement the recruiter set rather than supplying a value of their own.
 */
export type HardRequirementAnswerValue = boolean;

/**
 * One hard-requirement question.
 *
 * Carries the same seven fields as `RequirementField` plus its own copy: unlike the normal
 * questions, whose text is built from the dictionary, these arrive rendered in both languages with
 * every placeholder already substituted. Pick by the active language; do not translate them here.
 */
export type HardRequirementQuestionDTO = {
  question_key: HardRequirementQuestionKey;
  label_en: string;
  label_nl: string;
  job_requirements: HardRequirementJobRequirements;
  confirmed_info: HardRequirementAnswerValue | null;
  is_answered: boolean;
  /** Always `true`: the endpoint only returns questions the candidate still has to answer. */
  is_required: boolean;
  input_type: HardRequirementInputType;
  unit: string | null;
};

/**
 * Hard questions that ask the same thing as one of the normal questions.
 *
 * The two key spaces are disjoint — the overlap is semantic, not textual (`certificate` against
 * `certificates_and_licenses`) — so the pairing has to be declared. A hard question wins: it is the
 * gate, and asking the candidate the same thing twice on one page is the bug this prevents.
 */
export type SupersededQuestionKeys = Partial<Record<HardRequirementQuestionKey, QuestionKey>>;
